import { toApiNumber } from "../../../utils/formatters";

/**
 * Vendor / farmer payback uses REAL product amounts only:
 * - before commission (company profit stays with us)
 * - without cash-denomination "nearing" (price × qty/weight, not snapped totals)
 *
 * Many API payloads return net/remaining after commission and/or after IQD
 * denomination rounding; these helpers rebuild farmer-facing figures from lines.
 */

const money2 = (n) => Math.round(toApiNumber(n, 0) * 100) / 100;

/** Real line amount from unit price × billing qty/weight (no denomination snap).
 * Prefers net (after returns) qty/weight so farmer payback is not overstated.
 */
export function vendorItemAmount(item = {}) {
  const price = toApiNumber(
    item.unit_price
      ?? item.single_price
      ?? item.price
      ?? item.product?.price
      ?? item.product?.unit_price,
    Number.NaN
  );
  // Net remaining first; fall back to original sold only if net fields missing.
  const weight = toApiNumber(item.weight ?? item.sold_weight, 0);
  const qty = toApiNumber(item.qty ?? item.quantity ?? item.sold_qty, 0);
  const billing = weight > 0 ? weight : qty;
  if (Number.isFinite(price) && price > 0 && billing > 0) {
    return money2(price * billing);
  }
  // Prefer API subtotal (product amount) — never add commission onto net.
  const subtotal = toApiNumber(item.subtotal ?? item.sold_subtotal, 0);
  if (subtotal > 0) return money2(subtotal);
  return money2(item.net);
}

/** Sale-level farmer amount = sum of real line amounts (or product gross). */
export function vendorSaleAmount(sale = {}) {
  const items = Array.isArray(sale.items) ? sale.items : [];
  if (items.length) {
    return money2(items.reduce((sum, item) => sum + vendorItemAmount(item), 0));
  }
  const price = toApiNumber(sale.unit_price ?? sale.single_price ?? sale.price, Number.NaN);
  const weight = toApiNumber(sale.sold_weight ?? sale.weight, 0);
  const qty = toApiNumber(sale.sold_qty ?? sale.qty ?? sale.quantity, 0);
  const billing = weight > 0 ? weight : qty;
  if (Number.isFinite(price) && price > 0 && billing > 0) {
    return money2(price * billing);
  }
  const gross = toApiNumber(sale.gross_sold ?? sale.gross_total, 0);
  if (gross > 0) return money2(gross);
  // net_owed is already product-only on modern API — do not add commission.
  return money2(sale.net_owed);
}

/** Product-row farmer net = real gross − expenses (no commission). */
export function vendorProductNet(product = {}) {
  const gross = vendorProductGross(product);
  const expenses = money2(product.expenses);
  return money2(gross - expenses);
}

export function vendorProductGross(product = {}) {
  const price = toApiNumber(
    product.avg_price ?? product.unit_price ?? product.price ?? product.single_price,
    Number.NaN
  );
  const weight = toApiNumber(product.kg_sold, 0);
  const qty = toApiNumber(product.cases_sold, 0);
  const billing = weight > 0 ? weight : qty;
  if (Number.isFinite(price) && price > 0 && billing > 0) {
    return money2(price * billing);
  }
  return money2(product.gross ?? product.gross_sold);
}

/** Sum real product sales from a consignment payload. */
function rebuildGrossFromLines(report = {}) {
  const sales = Array.isArray(report.sales) ? report.sales : [];
  if (sales.length) {
    let sum = 0;
    let counted = false;
    sales.forEach((sale) => {
      const items = Array.isArray(sale.items) ? sale.items : [];
      if (items.length) {
        counted = true;
        items.forEach((item) => {
          sum += vendorItemAmount(item);
        });
      } else {
        const amt = vendorSaleAmount(sale);
        if (amt > 0) {
          counted = true;
          sum += amt;
        }
      }
    });
    if (counted) return money2(sum);
  }

  const products = Array.isArray(report.products) ? report.products : [];
  if (products.length) {
    return money2(products.reduce((sum, p) => sum + vendorProductGross(p), 0));
  }

  return null;
}

/** From GET /pos/vendor-consignment/:id (and similar consignment / archive payloads). */
export function vendorFinanceFromConsignment(report = {}) {
  const rebuiltGross = rebuildGrossFromLines(report);
  const expenses = money2(report.total_expenses);
  const paid = money2(report.total_paid);
  // Tracked for shop reports only — never added into farmer gross / net / remaining.
  const commission = money2(report.total_commission);
  const apiGross = money2(report.total_gross_sold ?? report.total_gross);
  const apiNet = report.total_net_owed != null ? money2(report.total_net_owed) : null;

  let gross;
  if (rebuiltGross != null && rebuiltGross > 0) {
    // Best: real price × qty from lines.
    gross = rebuiltGross;
  } else if (apiGross > 0) {
    // Product gross from API / archive row (already excludes commission).
    gross = apiGross;
  } else if (apiNet != null) {
    // Fallback: net + expenses (modern net is product − expenses, no commission cut).
    gross = money2(apiNet + expenses);
  } else {
    gross = 0;
  }

  const netOwed = money2(gross - expenses);
  const remaining = money2(netOwed - paid);

  return { gross, expenses, paid, commission, netOwed, remaining };
}

/** From GET /farmers-finance farmer rows. */
export function vendorFinanceFromFarmerRow(row = {}) {
  const gross = money2(row.gross);
  const expenses = money2(row.expenses);
  const paid = money2(row.paid);
  const commission = money2(row.commission);
  const apiNet = money2(row.net_owed);
  const apiRemaining = money2(row.remaining);

  const hasGrossBasis = gross > 0 || expenses > 0;
  // Add commission back so payback is before commission; cannot un-near here
  // without line items (those come from consignment endpoint).
  const netOwed = hasGrossBasis
    ? money2(gross - expenses)
    : money2(apiNet + commission);

  const remaining = hasGrossBasis
    ? money2(netOwed - paid)
    : money2(apiRemaining + commission);

  return {
    ...row,
    gross,
    expenses,
    paid,
    commission,
    net_owed: netOwed,
    remaining,
  };
}
